<template>
  <div
    class="c-machines-list"
    :class="{
      'c-machines-list_expanded': isExpanded,
      'c-machines-list_padding-bottom': nonVisibleMachinesCount === 0 && vendingMachines.length
    }"
  >
    <div
      v-if="!isLoading"
      class="c-machines-list-head"
    >
      <ContentTitle
        no-margin-bottom
        bolder
      >
        {{ title ?? 'Автоматы' }}
      </ContentTitle>

      <FilterByLocationCheckbox
        v-if="isFilteringByLocationAvailable && !isLocationLoading"
        :is-checked="!!isFilteringByLocationEnabled"
        :is-loading="!!isFilteringByLocationLoading"
        :error="vendingMachinesForUserLocationError"
        @update:is-checked="handleFilterByLocationCheckbox"
      />

      <CPreloader
        v-if="isLocationLoading"
        size="tiny"
        no-height
      />
    </div>

    <template v-if="vendingMachines.length">
      <div
        v-if="titles && !isMobile"
        class="c-machines-list-titles"
      >
        <div class="c-machines-list-titles__name">{{ titles.name ?? '' }}</div>
        <div class="c-machines-list-titles__link">{{ titles.link ?? '' }}</div>
      </div>

      <div
        v-for="machine in visibleVendingMachines"
        :key="machine.id"
        class="c-machines-list-item"
      >
        <div class="c-machines-list-item__title">
          {{ machine.attributes.address }}
        </div>

        <div class="c-machines-list-item__link-wrapper">
          <RouterLink
            :to="`/machine/${machine.id}`"
            class="c-machines-list-item__link"
          >
            <slot
              name="link"
              :machine="machine"
            />
          </RouterLink>
        </div>
      </div>

      <button
        v-if="!isExpanded && nonVisibleMachinesCount > 0"
        class="c-machines-list-bottom-button"
        :class="{
          'c-machines-list-bottom-button_rounded': isShowMoreButtonRounded
        }"
        @click="isExpanded = true"
      >
        Раскрыть список
      </button>
    </template>
  </div>

  <CPreloader v-if="isLoading" />

  <div
    v-if="!vendingMachines.length && !isLoading"
    class="c-machines-list-empty"
  >
    {{ emptyText || 'Список автоматов пуст' }}
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, defineProps } from 'vue';

  import { VendingMachine, VendingMachineWithProduct } from '@/api/requests/VendingMachines';
  import CPreloader from '../CPreloader/CPreloader.vue';
  import { useClientSize } from '@/composables/useClientSize';
  import ContentTitle from '../text/ContentTitle.vue';
  import FilterByLocationCheckbox from './inner/FilterByLocationCheckbox.vue';

  const MAX_VISIBLE_MACHINES = 7;

  const props = defineProps<{
    vendingMachines: VendingMachine[] | VendingMachineWithProduct[];

    titles?: {
      name?: string;
      link?: string;
    };

    isLoading?: boolean;
    isShowMoreButtonRounded?: boolean;
    emptyText?: string;
    title?: string;

    isLocationLoading?: boolean;
    isFilteringByLocationAvailable?: boolean;
    isFilteringByLocationLoading?: boolean;
    isFilteringByLocationEnabled?: boolean;

    vendingMachinesForUserLocationError?: Error | null;
  }>();

  const emit = defineEmits<{
    (e: 'update:filter-by-location-checkbox', value: boolean): void;
  }>();

  const { isMobile } = useClientSize();

  const isExpanded = ref(false);

  const visibleVendingMachines = computed<VendingMachine[] | VendingMachineWithProduct[]>(() =>
    isExpanded.value ? props.vendingMachines : props.vendingMachines.slice(0, MAX_VISIBLE_MACHINES)
  );

  const nonVisibleMachinesCount = computed(
    () => props.vendingMachines.length - visibleVendingMachines.value.length
  );

  function handleFilterByLocationCheckbox(isChecked: boolean) {
    emit('update:filter-by-location-checkbox', isChecked);
  }
</script>

<style scoped>
  .c-machines-list {
    padding: 24px 40px 0;
    display: flex;
    flex-direction: column;
    gap: 30px;

    &_expanded {
      padding-bottom: 48px;
    }

    &_padding-bottom {
      padding-bottom: 48px;
    }

    &-titles {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #2a2a2a;

      &__name {
        font-size: 24px;
        line-height: 30px;
        font-weight: 600;
      }

      &__link {
        font-size: 24px;
        font-weight: 600;
        line-height: 30px;
        width: 277px;
        text-align: center;
        width: 340px;
      }
    }
  }

  .c-machines-list-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .c-machines-list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__title {
      font-size: 24px;
      line-height: 30px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: 600;
    }

    &__link {
      font-size: 20px;
      background: var(--g-color-orange);
      padding: 8px 50px;
      color: #fff;
      text-decoration: none;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      text-align: center;
      border-radius: 40px;
      width: 100%;

      &-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 340px;
      }

      &:hover {
        opacity: 0.8;
      }
    }
  }

  .c-machines-list-bottom-button {
    margin-top: auto;
    font-size: 18px;
    line-height: 30px;
    background: var(--g-color-orange);
    padding: 0 50px;
    border-radius: 18px 18px 0 0;
    border: none;
    width: 300px;
    padding: 0;
    margin: 0 auto;
    color: #fff;
    cursor: pointer;
    font-weight: 600;

    &:hover {
      opacity: 0.8;
    }

    &_rounded {
      border-radius: 18px;
    }
  }

  .c-machines-list-empty {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    margin: 0;
    font-weight: 600;
    padding: 48px 0;
  }

  @media (--mobile) {
    .c-machines-list {
      padding: 12px 10px 0;

      &_padding-bottom {
        padding-bottom: 12px;
      }

      &-title {
        font-size: 26px !important;
      }

      &-empty {
        text-align: center;
      }
    }

    .c-machines-list-head {
      flex-direction: column;
      align-items: flex-start;
    }

    .c-machines-list-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;

      &__title {
        font-size: 18px;
        line-height: 24px;
        font-weight: 600;
        white-space: normal;
        word-break: break-word;
      }

      &__link {
        padding: 8px 0;
        height: auto;
        font-size: 20px;

        &-wrapper {
          width: 100%;
        }
      }
    }

    .c-machines-list-bottom-button {
      width: calc(100% - 20px);
      font-size: 18px;
      line-height: 28px;
    }
  }
</style>
